/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AfterViewInit, Component, ElementRef, Inject, NgZone, ViewChild} from '@angular/core';
import template from './invoice-translation-edit-page.component.html';
import * as _ from 'lodash';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {LOCALES} from '../common/constants/locales';
import {
  getInvoicePaymentProvider,
  getInvoiceTranslationKeys,
  initInvoiceTranslationModel,
  isInvoicePreviewSupported,
  normalizeInvoiceLogoUrl,
  prepareInvoiceTranslationForSubmit,
  updateInvoiceTranslationInfo
} from './invoice-translation-commons';
import {InvoiceTranslationPreviewResource} from '../../components/api/resources/resource/invoice-translation.resource';
import {DefaultInvoiceTranslationsResource} from '../../components/api/resources/resource/default-invoice-translations';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-invoice-translation-edit-page',
  template
})
export class InvoiceTranslationEditPageComponent extends AdminResourceEditPageBase implements AfterViewInit {
  @ViewChild('iframeContainer') iframeContainer: ElementRef;

  readonly locales = LOCALES;
  defaultTranslations: Record<string, any> = {};
  translationKeys: string[] = [];
  transDir = false;
  doingPreview = false;
  notValid = false;
  paymentProvider = '';
  private previewPending = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(DefaultInvoiceTranslationsResource) private defaultInvoiceTranslationsResource: any,
    @Inject(InvoiceTranslationPreviewResource) private InvoiceTranslationPreview: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.paymentProvider = getInvoicePaymentProvider(LocalNetworkProfile);
    this.notValid = !isInvoicePreviewSupported(this.paymentProvider);
    this.loadDefaultTranslations();
  }

  ngAfterViewInit(): void {
    if (this.previewPending) {
      this.previewPending = false;
      this.loadPreview();
    }
  }

  updateTranslations(): void {
    this.editingModel.translations = _.cloneDeep(this.defaultTranslations);
    this.markExternalFormDirty();
  }

  onLogoUrlBlur(): void {
    this.editingModel.logoUrl = normalizeInvoiceLogoUrl(this.editingModel.logoUrl);
  }

  protected afterInitEditingModel(): void {
    initInvoiceTranslationModel(this.editingModel, this.defaultTranslations);
    this.schedulePreview();
  }

  protected afterInitAfterSave(): void {
    updateInvoiceTranslationInfo(this.editingModel, this.defaultTranslations);
    this.schedulePreview();
  }

  protected onBeforeSubmit(): void {
    prepareInvoiceTranslationForSubmit(this.editingModel, this.defaultTranslations);
  }

  private schedulePreview(): void {
    if (this.isNew || !this.editingModel?.id) {
      return;
    }
    if (this.iframeContainer) {
      this.loadPreview();
      return;
    }
    this.previewPending = true;
  }

  private loadDefaultTranslations(): void {
    this.defaultInvoiceTranslationsResource.get().then((defaults: Record<string, any>) => {
      this.defaultTranslations = defaults || {};
      this.translationKeys = getInvoiceTranslationKeys(this.defaultTranslations);
      if (this.editingModel) {
        initInvoiceTranslationModel(this.editingModel, this.defaultTranslations);
      }
    });
  }

  private loadPreview(): void {
    if (!this.editingModel?.id) {
      return;
    }
    this.doingPreview = true;
    this.InvoiceTranslationPreview.preview(this.editingModel.id, this.paymentProvider).then((preview: any) => {
      this.doingPreview = false;
      this.setupIframe(preview.html);
    }).catch(() => {
      this.doingPreview = false;
    });
  }

  private setupIframe(html: string): void {
    const divContainer = this.iframeContainer?.nativeElement;
    if (!divContainer) {
      return;
    }
    divContainer.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allowTransparency', 'true');
    iframe.setAttribute('id', 'iframe-window');
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', '700px');
    iframe.setAttribute('style', 'outline: 2px solid #bbb;display:block;borderWidth:0;padding:0;margin:0;');
    iframe.frameBorder = '0';
    divContainer.appendChild(iframe);
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();
  }
}
